import React, { useState, useEffect } from "react";
import Button from "@/components/atoms/Button";
import { Plus, Pencil, Trash2 } from "lucide-react";
import Note from "@/types/Note";
import Ticket from "@/types/Ticket";
import axios from "axios";
import { toast } from "react-toastify";
import useAccess from "@/hooks/useAccess";
import TextArea from "@/components/atoms/TextArea";

type Props = {
  ticket: Ticket;
  ticketNotes: Note[];
  setTicketNotes: (notes: Note[]) => void;
};

const NoteTab = ({ ticket, ticketNotes, setTicketNotes }: Props) => {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteMessage, setNewNoteMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteMessage, setEditingNoteMessage] = useState("");
  const { currentUser } = useAccess();

  const formatDate = (input: string): string => {
    const date = new Date(input);
    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };
    return date.toLocaleDateString("en-GB", options).replace(",", "");
  };

  const getCsrfToken = () => {
    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return tokenMeta ? tokenMeta.getAttribute("content") || "" : "";
  };

  const addNote = async () => {
    setIsSubmitting(true);
    try {
      const csrfToken = getCsrfToken();
      const response = await axios.post(
        `/notes`,
        {
          note: {
            message: newNoteMessage,
            ticket_id: ticket.id,
          },
        },
        {
          headers: {
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (response.data.success) {
        const newNote: Note = { 
          id: response.data.note.id,
          message: response.data.note.message,
          user: { id: currentUser?.id || 0, name: currentUser?.name || "" },
          created_at: response.data.note.created_at
        };

        setTicketNotes([...ticketNotes, newNote]);
        setNewNoteMessage("");
        setIsAddingNote(false);
        toast.success("Note added successfully.");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to add the note. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (noteId: number) => {
    try {
      const csrfToken = getCsrfToken();
      const response = await axios.delete(`/notes/${noteId}`, {
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      });

      if (response.data.success) {
        setTicketNotes(ticketNotes.filter((note: Note) => note.id !== noteId));
        toast.success("Note deleted successfully.");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to delete the note. Please try again.");
    }
  };

  const handleEdit = (note: Note) => {
    setEditingNoteId(note.id);
    setEditingNoteMessage(note.message);
  };

  const handleUpdate = async () => {
    if (!editingNoteId) return;

    setIsSubmitting(true);
    try {
      const csrfToken = getCsrfToken();
      const response = await axios.put(
        `/notes/${editingNoteId}`,
        {
          note: {
            message: editingNoteMessage,
          },
        },
        {
          headers: {
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (response.data.success) {
        ticketNotes.filter((note: Note) => note.id === editingNoteId)[0].message = editingNoteMessage;
        setTicketNotes([...ticketNotes]);
        setEditingNoteId(null);
        setEditingNoteMessage("");
        toast.success("Note updated successfully.");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to update the note. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 border border-border bg-background rounded-lg shadow-sm">
      <h2 className="text-lg font-semibold mb-4">Notes</h2>
      <div className="flex items-center flex-wrap gap-4">
        {ticketNotes.map((note) => {
          const isCurrentUser = note.user?.id === currentUser?.id;

          return (
            <div
              key={note.id}
              className=" bg-yellow-100 border border-border dark:bg-background p-4 rounded-md shadow-sm w-full md:w-auto md:max-w-sm min-w-[300px]"
            >
              {editingNoteId === note.id ? (
                <div>
                  <TextArea
                    onChange={(e) => setEditingNoteMessage(e.target.value)}
                    value={editingNoteMessage}
                    className="w-full p-2 border dark:border-border rounded-md shadow-sm mb-2"
                    rows={3}
                  />

                  <div className="flex gap-2">
                    <Button onClick={handleUpdate} type="submit" disabled={isSubmitting}>
                      {isSubmitting ? "Updating..." : "Update"}
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingNoteId(null);
                        setEditingNoteMessage("");
                      }}
                      type="button"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-start">
                    <p className="text-sm whitespace-pre-line">{note.message}</p>
                  </div>
                  <div className="flex justify-end">
                    <div className="text-right">
                      <div className="flex mt-4">
                        {isCurrentUser && (
                          <div className="flex justify-end items-center space-x-2">
                            <Trash2
                              size={16}
                              className="cursor-pointer text-red-500 hover:text-red-700"
                              onClick={() => handleDelete(note.id)}
                            />
                            <Pencil
                              size={16}
                              className="cursor-pointer text-gray-500 hover:text-gray-800"
                              onClick={() => handleEdit(note)}
                            />
                          </div>
                        )}
                        <p className="ml-4 text-xs font-semibold">{note.user.name}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Posted on {formatDate(note.created_at)}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}

        {!isAddingNote ? (
          <Button onClick={() => setIsAddingNote(true)} type="button">
            <Plus /> Add note
          </Button>
        ) : (
          <div className="w-80">
            <TextArea
              onChange={(e) => setNewNoteMessage(e.target.value)}
              value={newNoteMessage}
              placeholder="Write your note here..."
              className="w-full p-2 border dark:border-border rounded-md shadow-sm mb-3 dark:placeholder-darkTextColor"
              rows={3}
            />
           
            <div className="flex gap-2">
              <Button onClick={addNote} type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Adding..." : "Submit"}
              </Button>
              <Button
                onClick={() => {
                  setIsAddingNote(false);
                  setNewNoteMessage("");
                }}
                type="button"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NoteTab;